import { useState, useRef } from 'react';
import { FaUpload, FaCheck, FaTimes } from 'react-icons/fa';

type FileUploadProps = {
  onFileSelected: (file: File | null) => void;
};

export default function FileUpload({ onFileSelected }: FileUploadProps) {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    
    if (!file) {
      setSelectedFile(null);
      setPreviewUrl(null);
      onFileSelected(null);
      return;
    }
    
    // Check file type
    const validTypes = ['image/jpeg', 'image/png', 'image/jpg', 'application/pdf'];
    if (!validTypes.includes(file.type)) {
      setError('Please upload a valid file (JPEG, PNG, or PDF)');
      return;
    }
    
    // Check file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError('File size should be less than 5MB');
      return;
    }
    
    setError(null);
    setSelectedFile(file);
    onFileSelected(file);
    
    // Create preview for images
    if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = () => {
        setPreviewUrl(reader.result as string);
      };
      reader.readAsDataURL(file);
    } else {
      // For PDFs, just show an icon or text
      setPreviewUrl(null);
    }
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    setPreviewUrl(null);
    onFileSelected(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <label className="block text-sm font-medium text-gray-700">
          Upload Payment Receipt
        </label>
        {selectedFile && (
          <button
            type="button"
            onClick={handleRemoveFile}
            className="text-sm text-red-600 hover:text-red-800"
          >
            Remove
          </button>
        )}
      </div>
      
      {!selectedFile ? (
        <div 
          onClick={triggerFileInput}
          className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md cursor-pointer hover:bg-gray-50"
        >
          <div className="space-y-1 text-center">
            <FaUpload className="mx-auto h-12 w-12 text-gray-400" />
            <div className="flex text-sm text-gray-600">
              <label
                htmlFor="file-upload"
                className="relative cursor-pointer bg-white rounded-md font-medium text-blue-600 hover:text-blue-500 focus-within:outline-none"
              >
                <span>Upload a file</span>
                <input
                  id="file-upload"
                  name="file-upload"
                  type="file"
                  className="sr-only"
                  ref={fileInputRef}
                  onChange={handleFileChange}
                  accept="image/jpeg,image/png,image/jpg,application/pdf"
                />
              </label>
              <p className="pl-1">or drag and drop</p>
            </div>
            <p className="text-xs text-gray-500">
              PNG, JPG, JPEG or PDF up to 5MB
            </p>
          </div>
        </div>
      ) : (
        <div className="mt-1 flex items-center p-4 border border-gray-300 rounded-md bg-gray-50">
          {previewUrl ? (
            <div className="flex items-center">
              <div className="w-16 h-16 mr-4 relative rounded-md overflow-hidden">
                <img src={previewUrl} alt="Preview" className="object-cover w-full h-full" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-900 truncate">{selectedFile.name}</p>
                <p className="text-xs text-gray-500">
                  {(selectedFile.size / 1024).toFixed(1)} KB
                </p>
              </div>
              <div className="ml-4">
                <FaCheck className="text-green-500" />
              </div>
            </div>
          ) : (
            <div className="flex items-center">
              <div className="w-16 h-16 mr-4 flex items-center justify-center bg-gray-200 rounded-md">
                <span className="text-xs font-medium text-gray-500">PDF</span>
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-900 truncate">{selectedFile.name}</p>
                <p className="text-xs text-gray-500">
                  {(selectedFile.size / 1024).toFixed(1)} KB
                </p>
              </div>
              <div className="ml-4">
                <FaCheck className="text-green-500" />
              </div>
            </div>
          )}
        </div>
      )}
      
      {error && (
        <div className="flex items-center text-sm text-red-600">
          <FaTimes className="mr-1" />
          {error}
        </div>
      )}
    </div>
  );
}
